import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Activity, Loader2, Search } from "lucide-react";
import { debounce, parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs";
import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { api, type RunListParams } from "../api";
import { FlatTable } from "../components/flat-table";
import { EmptyState, PageHeader } from "../components/page";
import { RunHistoryRow } from "../components/runs/run-history-row";
import { RunSheet } from "../components/runs/run-sheet";
import { STATION_SORTS, StationComparison, type StationSort } from "../components/runs/station-comparison";
import { SortHead } from "../components/sort-head";
import { ColorDot } from "../components/status-badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { Skeleton } from "../components/ui/skeleton";
import { TableBody, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs } from "../components/ui/tabs";
import { ViewTab, ViewTabsList, ViewToolbar } from "../components/view-tabs";
import { useDebouncedValue } from "../hooks/use-debounced-value";
import { useFactory } from "../store";
import type { RunHistoryItem, RunSort } from "../types";

const HOUR_MS = 60 * 60 * 1000;
const RANGES = [
  { value: "24h", label: "Last 24 hours", ms: 24 * HOUR_MS },
  { value: "7d", label: "Last 7 days", ms: 7 * 24 * HOUR_MS },
  { value: "30d", label: "Last 30 days", ms: 30 * 24 * HOUR_MS },
  { value: "90d", label: "Last 90 days", ms: 90 * 24 * HOUR_MS },
  { value: "all", label: "All time", ms: null },
] as const;

const OUTCOMES = [
  { value: "active", label: "In progress", status: "queued,blocked,launching,running,waiting_for_input" },
  { value: "completed", label: "Completed", status: "completed" },
  { value: "failed", label: "Failed or expired", status: "failed,expired" },
  { value: "cancelled", label: "Cancelled", status: "cancelled" },
] as const;

const SORTS: readonly RunSort[] = ["created", "duration", "cost", "tokens"];
const ALL = "all";
const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 250;
const REFRESH_MS = 10_000;

/** Filters, sorts, the open run and the tab live in the URL, so a comparison can be shared. */
const runParams = {
  view: parseAsStringLiteral(["runs", "stations"]).withDefault("runs"),
  range: parseAsStringLiteral(RANGES.map((r) => r.value)).withDefault("7d"),
  station: parseAsString,
  outcome: parseAsStringLiteral(OUTCOMES.map((o) => o.value)),
  verdict: parseAsString,
  lane: parseAsString,
  provider: parseAsString,
  q: parseAsString.withDefault("").withOptions({ limitUrlUpdates: debounce(SEARCH_DEBOUNCE_MS) }),
  sort: parseAsStringLiteral(SORTS).withDefault("created"),
  dir: parseAsStringLiteral(["asc", "desc"]).withDefault("desc"),
  rank: parseAsStringLiteral(STATION_SORTS).withDefault("runs"),
  rankDir: parseAsStringLiteral(["asc", "desc"]).withDefault("desc"),
  run: parseAsString.withOptions({ history: "push" }),
};

export function RunsPage() {
  const [params, setParams] = useQueryStates(runParams);
  const { agentById, layout, state } = useFactory();
  const { view, range, station, outcome, verdict, lane, provider, sort, dir: direction } = params;
  const q = useDebouncedValue(params.q.trim(), SEARCH_DEBOUNCE_MS);

  // Fixed when the range is picked, so the query key stays put between refetches.
  const createdAfter = useMemo(() => {
    const ms = RANGES.find((r) => r.value === range)?.ms;
    return ms ? new Date(Date.now() - ms).toISOString() : undefined;
  }, [range]);
  const filter: RunListParams = {
    station: station ?? undefined,
    status: OUTCOMES.find((o) => o.value === outcome)?.status,
    verdict: verdict ?? undefined,
    lane: lane ?? undefined,
    provider: provider ?? undefined,
    q: q || undefined,
    createdAfter,
  };

  const runsQuery = useInfiniteQuery({
    queryKey: ["runs", filter, sort, direction],
    queryFn: ({ pageParam }) => api.runs({ ...filter, sort, direction, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastOffset) =>
      lastPage.runs.length === PAGE_SIZE ? lastOffset + PAGE_SIZE : undefined,
    refetchInterval: REFRESH_MS,
    placeholderData: (previous) => previous,
  });
  const stationsQuery = useQuery({
    queryKey: ["run-stations", filter],
    queryFn: () => api.runStations(filter),
    refetchInterval: REFRESH_MS,
    placeholderData: (previous) => previous,
  });

  // Offsets shift as new runs arrive between page fetches, so a run can appear on two pages.
  const runs = useMemo(() => {
    const seen = new Set<string>();
    const out: RunHistoryItem[] = [];
    for (const page of runsQuery.data?.pages ?? []) {
      for (const run of page.runs) {
        if (seen.has(run.id)) continue;
        seen.add(run.id);
        out.push(run);
      }
    }
    return out;
  }, [runsQuery.data]);
  const stations = stationsQuery.data?.stations ?? [];
  const total = stations.reduce((sum, row) => sum + row.runs, 0);

  const stationOptions = (layout?.sections ?? []).flatMap((section) => section.agents);
  const lanes = Object.entries(layout?.lanes ?? {});
  const providers = Object.keys(state?.providers ?? {});
  const verdicts = useMemo(() => {
    const scope = station ? [agentById.get(station)] : [...agentById.values()];
    const found = new Set(scope.flatMap((agent) => (agent ? Object.keys(agent.routing.onVerdict) : [])));
    for (const row of stations) for (const key of Object.keys(row.verdicts)) found.add(key);
    if (verdict) found.add(verdict);
    return [...found].sort();
  }, [agentById, station, stations, verdict]);

  const filtered = Boolean(station || outcome || verdict || lane || provider || params.q);
  const clearFilters = () =>
    setParams({ station: null, outcome: null, verdict: null, lane: null, provider: null, q: null });
  const toggleSort = (key: RunSort) =>
    setParams(key === sort ? { dir: direction === "desc" ? "asc" : "desc" } : { sort: key, dir: "desc" });
  const toggleRank = (key: StationSort) =>
    setParams(
      key === params.rank
        ? { rankDir: params.rankDir === "desc" ? "asc" : "desc" }
        : { rank: key, rankDir: "desc" },
    );
  const sortHead = (key: RunSort, label: string, className: string) => (
    <SortHead
      label={label}
      active={sort === key}
      direction={direction}
      onSort={() => toggleSort(key)}
      className={className}
    />
  );

  const selectedRow = params.run ? runs.find((run) => run.id === params.run) : undefined;
  const loading = view === "runs" ? runsQuery.isLoading : stationsQuery.isLoading;
  const empty = view === "runs" ? runs.length === 0 : stations.length === 0;
  const stale = view === "runs" ? runsQuery.isPlaceholderData : stationsQuery.isPlaceholderData;

  return (
    <>
      <PageHeader
        title="Runs"
        description="Every station run on every work order. Sort them, or compare stations side by side."
      />
      <div className="flex flex-col">
        <ViewToolbar
          tabs={
            <Tabs value={view} onValueChange={(v) => setParams({ view: v as typeof view })}>
              <ViewTabsList>
                <ViewTab value="runs">Runs</ViewTab>
                <ViewTab value="stations">By station</ViewTab>
              </ViewTabsList>
            </Tabs>
          }
        >
          {filtered && (
            <Button variant="ghost" size="sm" className="h-7 text-muted-foreground" onClick={clearFilters}>
              Clear
            </Button>
          )}
          <Select value={range} onValueChange={(v) => setParams({ range: v as typeof range })}>
            <SelectTrigger size="sm" className="h-7 w-36 text-xs" aria-label="Time range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGES.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={station ?? ALL}
            onValueChange={(v) => setParams({ station: v === ALL ? null : v, verdict: null })}
          >
            <SelectTrigger size="sm" className="h-7 w-44 text-xs" aria-label="Filter by station">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All stations</SelectItem>
              {stationOptions.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  <ColorDot color={agent.station.color} />
                  {agent.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={outcome ?? ALL}
            onValueChange={(v) => setParams({ outcome: v === ALL ? null : (v as typeof outcome) })}
          >
            <SelectTrigger size="sm" className="h-7 w-36 text-xs" aria-label="Filter by outcome">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Any outcome</SelectItem>
              {OUTCOMES.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {verdicts.length > 0 && (
            <Select
              value={verdict ?? ALL}
              onValueChange={(v) => setParams({ verdict: v === ALL ? null : v })}
            >
              <SelectTrigger size="sm" className="h-7 w-40 text-xs" aria-label="Filter by verdict">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any verdict</SelectItem>
                {verdicts.map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={lane ?? ALL} onValueChange={(v) => setParams({ lane: v === ALL ? null : v })}>
            <SelectTrigger size="sm" className="h-7 w-32 text-xs" aria-label="Filter by lane">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All lanes</SelectItem>
              {lanes.map(([id, l]) => (
                <SelectItem key={id} value={id}>
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {providers.length > 1 && (
            <Select
              value={provider ?? ALL}
              onValueChange={(v) => setParams({ provider: v === ALL ? null : v })}
            >
              <SelectTrigger size="sm" className="h-7 w-32 text-xs" aria-label="Filter by provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All providers</SelectItem>
                {providers.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="relative w-full sm:w-56">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={params.q}
              onChange={(e) => setParams({ q: e.target.value })}
              placeholder="Work order title or issue key"
              className="h-7 bg-muted/40 pl-8 text-xs shadow-none"
              aria-label="Search runs by work order"
            />
          </div>
        </ViewToolbar>

        {loading ? (
          <div className="-mx-gutter divide-y divide-border/60">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex h-10 items-center gap-4 px-gutter">
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-3.5 flex-1" />
                <Skeleton className="h-5 w-20 rounded-full" />
              </div>
            ))}
          </div>
        ) : empty ? (
          <EmptyState
            icon={Activity}
            className="mt-6"
            title={filtered ? "No matching runs" : "No runs in this range"}
            description={
              filtered
                ? "Try a different station, outcome or search, or a longer time range."
                : "Pick a longer time range, or start a work order."
            }
          />
        ) : view === "stations" ? (
          <StationComparison
            stations={stations}
            agentById={agentById}
            sort={params.rank}
            direction={params.rankDir}
            onSort={toggleRank}
            onOpen={(agentId) => setParams({ view: "runs", station: agentId })}
            className={cn(stale && "opacity-60")}
          />
        ) : (
          <>
            <FlatTable className={cn(stale && "opacity-60")}>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-44">Station</TableHead>
                  <TableHead>Work order</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Verdict</TableHead>
                  <TableHead className="hidden xl:table-cell">Provider</TableHead>
                  {sortHead("duration", "Duration", "hidden sm:table-cell")}
                  {sortHead("tokens", "Tokens", "hidden xl:table-cell")}
                  {sortHead("cost", "Cost", "hidden lg:table-cell")}
                  {sortHead("created", "Created", "hidden lg:table-cell")}
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => (
                  <RunHistoryRow
                    key={run.id}
                    run={run}
                    agent={agentById.get(run.agentId)}
                    selected={run.id === params.run}
                    onOpen={() => setParams({ run: run.id })}
                  />
                ))}
              </TableBody>
            </FlatTable>
            <div className="flex items-center justify-between gap-2 py-3 text-xs text-muted-foreground">
              <span className="tabular-nums">
                {runs.length >= total
                  ? `${runs.length.toLocaleString()} run${runs.length === 1 ? "" : "s"}`
                  : `${runs.length.toLocaleString()} of ${total.toLocaleString()} runs`}
              </span>
              {runsQuery.isFetchingNextPage ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 className="size-3.5 animate-spin" /> Loading more
                </span>
              ) : runsQuery.hasNextPage ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => void runsQuery.fetchNextPage()}
                >
                  Load more
                </Button>
              ) : null}
            </div>
          </>
        )}
      </div>

      <Sheet open={Boolean(params.run)} onOpenChange={(open) => !open && setParams({ run: null })}>
        <SheetContent className="gap-0 data-[side=right]:w-full data-[side=right]:sm:max-w-2xl data-[side=right]:lg:max-w-3xl">
          {params.run && <RunSheet runId={params.run} row={selectedRow} />}
        </SheetContent>
      </Sheet>
    </>
  );
}
